// askSmart-ui/src/pages/TOSP/components/AssignUnitsStep.tsx
import React from "react";
import { UnitOption } from "./OccupancyCard";

/** A space the lease turned out to cover. */
export interface DetectedSpaceChoice {
  id: string;
  name: string;
  /** True for an area the reviewer added rather than one read from the lease. */
  added?: boolean;
}

interface AssignUnitsStepProps {
  spaces: DetectedSpaceChoice[];
  unitOptions: UnitOption[];
  /** The units chosen so far, keyed by space id. */
  chosen: Record<string, string>;
  /**
   * A unit picked for a space.
   *
   * Handled by the caller rather than kept here, because choosing a unit
   * somebody else still holds isn't a selection — it's a handover, and the
   * takeover wizard has to run before the assignment sticks.
   */
  onChoose: (spaceId: string, unitId: string) => void;
  /** An area the lease didn't yield — added by hand. */
  onAddSpace: () => void;
  /** An area the lease shouldn't have yielded, or that was added in error. */
  onRemoveSpace: (spaceId: string) => void;
  /** Naming an added area. Areas read from the lease keep the lease's name. */
  onRenameSpace: (spaceId: string, name: string) => void;
  onBack: () => void;
  onClose: () => void;
  onNext: () => void;
}

// Step 2, once the lease has been read: one dropdown per space it covers.
//
// The list starts from what the lease yielded but isn't fixed to it. Lease
// forms differ by landlord, and a form the rules haven't met can produce one
// area too few or one too many — neither of which should leave the reviewer
// stuck on a step they can't complete or can't correct.
export default function AssignUnitsStep({
  spaces,
  unitOptions,
  chosen,
  onChoose,
  onAddSpace,
  onRemoveSpace,
  onRenameSpace,
  onBack,
  onClose,
  onNext,
}: AssignUnitsStepProps) {
  const complete =
    spaces.length > 0 &&
    spaces.every((space) => chosen[space.id] && space.name.trim());

  return (
    <>
      <div className="smrtdocs-modal-header tosp-wizard-header">
        <h2 style={{ margin: 0, fontSize: "1rem" }}>Assign Units</h2>
        <button
          type="button"
          className="tosp-wizard-close"
          aria-label="Cancel"
          title="Cancel"
          onClick={onClose}
        >
          ×
        </button>
      </div>

      <div className="smrtdocs-modal-body">
        {spaces.length === 0 && (
          <p className="tosp-wizard-note">
            No leased areas were found in this lease. Add them below.
          </p>
        )}

        {spaces.map((space) => (
          <div key={space.id} className="tosp-assign-row">
            <div className="tosp-field tosp-assign-area">
              {space.added ? (
                <input
                  className="manage-input"
                  value={space.name}
                  placeholder="Area name"
                  aria-label="Area name"
                  onChange={(e) => onRenameSpace(space.id, e.target.value)}
                />
              ) : (
                <label className="tosp-label" htmlFor={`tosp-wizard-unit-${space.id}`}>
                  {space.name}
                </label>
              )}
            </div>

            <select
              id={`tosp-wizard-unit-${space.id}`}
              className="manage-input"
              value={chosen[space.id] || ""}
              onChange={(e) => onChoose(space.id, e.target.value)}
            >
              <option value="">— Select a unit —</option>
              {/* Somebody else's unit is offered, not hidden: taking it on is a
                  handover, which the takeover wizard walks through. It is shown
                  greyed rather than removed when handovers are blocked — the
                  occupant's name is what tells the admin they've picked the
                  wrong unit, and a unit that simply vanished would read as one
                  that doesn't exist. */}
              {unitOptions.map(({ unit, label, occupiedBy, disabled }) => (
                <option key={unit.id} value={unit.id} disabled={disabled}>
                  {label}
                  {occupiedBy ? ` — occupied by ${occupiedBy}` : ""}
                </option>
              ))}
            </select>

            <button
              type="button"
              className="tosp-assign-remove"
              title={`Remove ${space.name || "this area"}`}
              aria-label={`Remove ${space.name || "this area"}`}
              onClick={() => onRemoveSpace(space.id)}
            >
              ×
            </button>
          </div>
        ))}

        <button type="button" className="tosp-add-area" onClick={onAddSpace}>
          + Add Leased Area
        </button>
      </div>

      <div className="tosp-wizard-actions">
        <button type="button" className="btn-cancel" onClick={onBack}>
          Back
        </button>
        <button
          type="button"
          className="tosp-wizard-next"
          disabled={!complete}
          onClick={onNext}
        >
          Next
        </button>
      </div>
    </>
  );
}
